import { useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { Grid, GridContainer } from "@trussworks/react-uswds";
import { Marker } from "react-leaflet";
import styled from "styled-components";
import BackButton from "../components/BackButton";
import PrintButton from "../components/PrintButton";
import ResultsMap from "../components/Search/ResultsMap";
import ShareButton from "../components/ShareButton";
import { V1Clinic, ClinicLocation } from "../types/v1_schemas";
import { AnalyticsAction, logEvent, logPageView } from "../utils/analytics";
import { useTranslation } from "react-i18next";
import CompareDetail from "../components/Compare/CompareDetail";
import HoursTable from "../components/Compare/HoursTable";
import FeesTable from "../components/Compare/FeesTable";
import PopulationsTable from "../components/Compare/PopulationsTable";
import LanguagesTable from "../components/Compare/LanguagesTable";
import MentalHealthServicesTable from "../components/Compare/MentalHealthServicesTable";
import AccessibilityTable from "../components/Compare/AccessibilityTable";
import TelehealthTable from "../components/Compare/TelehealthTable";
import TelehealthOnlyMap from "../components/TelehealthOnlyMap";
import { QuerySingleApi } from "../utils/QueryHelper";
import { AxiosResponse } from "axios";
import { getLatLng, getMapMarker, getResultBounds } from "../utils/map";
import { getDistanceToZipCenter } from "../utils/search";
import { handlePageLoad } from "../utils/accessibility";
import SubstanceUseServicesTable from "../components/Compare/SubstanceUseServicesTable";

const ShadowHR = styled.hr`
  box-shadow: 0 4px 8px 0 black;
  @media print
    {    
      display: none !important;
    }
`;

export default function Compare() {
  console.log(">>>>>> IN Compare.ts.x >>>>>");
  //const { state }: any = useLadderData();

  const { t, i18n } = useTranslation();
  const [providerA, setProviderA] = useState<V1Clinic>();
  const [providerB, setProviderB] = useState<V1Clinic>();

  const [params] = useSearchParams();
  const ids = params.getAll("id");
  const zip = params.get("zip");
  const location = useLocation();

  let { prevSearch } = (location.state ?? {}) as {
    prevSearch?: string;
  };
  
  useEffect(() => {
    const errorFn = (error: any) => {
      console.error(`Result Detail: Axios Error: ${error.message}`);
    };
    
      const responseFnA = (response: AxiosResponse) => {
      if (response.status === 200 && response.data) {
        let prov = response.data as V1Clinic;
        prov.searchRank = 1;
  
        let primAddr : ClinicLocation | null = response.data?.clinicLocations?.filter((element:ClinicLocation) => {
          return (
            //element.addrTypeText === "Primary Address" &&
            element.latitude?.length &&
            element.longitude?.length
          );
        })[0];
  
        if (primAddr){
          prov.latLng = getLatLng(primAddr?.latitude ?? "", primAddr.longitude ?? "");
          prov.distanceInMiles = getDistanceToZipCenter(zip, prov);
        }
  
        setProviderA(prov);
      } else {
        console.error(`Result Detail: HTTP error! Status: ${response.status}`);
      }
    };
    
    const responseFnB = (response: AxiosResponse) => {
      if (response.status === 200 && response.data) {
        let prov = response.data as V1Clinic;
        prov.searchRank = 2;
  
        let primAddr : ClinicLocation | null = response.data?.clinicLocations?.filter((element:ClinicLocation) => {
          return (
            //element.addrTypeText === "Primary Address" &&
            element.latitude?.length &&
            element.longitude?.length
          );
        })[0];
  
        if (primAddr){
          prov.latLng = getLatLng(primAddr?.latitude ?? "", primAddr.longitude ?? "");
          prov.distanceInMiles = getDistanceToZipCenter(zip, prov);
        }
  
        setProviderB(prov);
      } else {
        console.error(`Result Detail: HTTP error! Status: ${response.status}`);
      }
    };
    
    QuerySingleApi(ids[0], i18n.language, new AbortController().signal, responseFnA, errorFn);
    QuerySingleApi(ids[1], i18n.language, new AbortController().signal, responseFnB, errorFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, i18n.language]);

  useEffect(() => {
    handlePageLoad();
    logPageView();
    const initialStickyHeaderTop =
      document.getElementById("sticky-header")?.offsetTop;

    const scroll = () => {
      const currentStickyHeaderTop =
        document.getElementById("sticky-header")?.offsetTop;
      if (
        !!currentStickyHeaderTop &&
        !!initialStickyHeaderTop &&
        currentStickyHeaderTop > initialStickyHeaderTop
      ) {
        if (
          document
            .getElementById("shadow-line")
            ?.classList.contains("display-none")
        ) {
          document
            .getElementById("shadow-line")
            ?.classList.remove("display-none");
        }
      } else {
        document.getElementById("shadow-line")?.classList.add("display-none");
      }
    };
    
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", scroll);
      return window.addEventListener("scroll", scroll);
    }
  }, []);

  if (!ids || ids.length !== 2 || ids[0] === ids[1]) {
    return <Navigate replace to="/" />;
  }

  return (
    providerA && providerB ? 
    <GridContainer>
      <div className="margin-y-2 display-flex flex-justify">
        <BackButton
          onClick={() => logEvent(AnalyticsAction.ReturnToSearch)}
          text={t("backToSearch")}
          href={`/search${prevSearch ?? ""}`}
        />
        <div className="display-flex flex-align-center">
          <PrintButton className="margin-right-3" />
          <ShareButton text={t("detailsPageShare")} />
        </div>
      </div>
      <Grid row>
        <Grid col={12}>
          <h1 className="margin-top-0">{t("compareLocations")}</h1>
          {providerA.latLng || providerB.latLng ? (
            <ResultsMap
              bounds={getResultBounds([providerA, providerB])}
              mapHeight="300px"
            >
              {providerA.latLng && (
                <Marker
                  title={providerA.accountId}
                  position={providerA.latLng}
                  icon={getMapMarker(providerA)}
                  key={providerA.accountId}
                  interactive={false}
                />
              )}
              {providerB.latLng && (
                <Marker
                  title={providerB.accountId}
                  position={providerB.latLng}
                  icon={getMapMarker(providerB)}
                  key={providerB.accountId}
                  interactive={false}
                />
              )}
            </ResultsMap>
          ) : (
            <TelehealthOnlyMap alertMessage={t("compareTelehealthMapAlert")} />
          )}
        </Grid>
      </Grid>
      <Grid
        row
        gap="md"
        className="position-sticky top-0 bg-white z-top"
        id="sticky-header"
      >
        <Grid col={6} tablet={{ col: 5, offset: 2 }}>
          <p className="margin-y-2 text-bold">{providerA.searchRank}.</p>
          <Link className="usa-link" to={`/result/${providerA.accountId}`}>
            <h2 className="margin-top-0 margin-bottom-3">
              {providerA.displayName}
            </h2>
          </Link>
        </Grid>
        <Grid col={6} tablet={{ col: 5 }}>
          <p className="margin-y-2 text-bold">{providerB.searchRank}.</p>
          <Link className="usa-link" to={`/result/${providerB.accountId}`}>
            <h2 className="margin-top-0 margin-bottom-3">
              {providerB.displayName}
            </h2>
          </Link>
        </Grid>
        <ShadowHR
          as="hr"
          id="shadow-line"
          className="margin-y-0 width-full display-none"
        />
      </Grid>
      <Grid row gap="md" className="margin-top-1">
        <Grid col={6} tablet={{ col: 5, offset: 2 }}>
          <CompareDetail data={providerA} zip={zip} />
        </Grid>
        <Grid col={6} tablet={{ col: 5 }}>
          <CompareDetail data={providerB} zip={zip} />
        </Grid>
      </Grid>
      <hr className="margin-y-4" />
      <TelehealthTable providerA={providerA} providerB={providerB} />

      <HoursTable providerA={providerA} providerB={providerB} />
      <FeesTable providerA={providerA} providerB={providerB} />

      { (providerA.clinicServicesProvideds?.length ||
          providerA.clinicServicesProvideds?.length
         ) && (
          <>
            <h2>{t("services")}</h2>
            <hr className="margin-bottom-3" />
          </>
        )
      }
      <MentalHealthServicesTable providerA={providerA} providerB={providerB} />
      <SubstanceUseServicesTable providerA={providerA} providerB={providerB} />

      <h2>{t("details")}</h2>
      <hr className="margin-bottom-3" />
      <AccessibilityTable providerA={providerA} providerB={providerB} />
      <LanguagesTable providerA={providerA} providerB={providerB} />
      <PopulationsTable providerA={providerA} providerB={providerB} />
    </GridContainer>
    : <></>
  );
}
